// Builds UNIQUE per-app SEO metadata and page content.
// Uses many phrasing variants selected deterministically from the app slug,
// so each app gets its own distinct title, description and body — but stable
// across visits (important for Google indexing).

export const slugToWords = (slug = '') =>
  slug
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

// -------------- Deterministic pickers --------------
const hash = (str) => {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h * 16777619) >>> 0;
  }
  return h;
};
const pick = (arr, slug, salt) => arr[hash(slug + '|' + salt) % arr.length];

// -------------- Title variants (English) --------------
const titleVariants = [
  (n, a) => `${n} APK Download — Free ₹${a.bonus} Sign-up Bonus | GoGoYonoGames`,
  (n, a) => `Download ${n} Latest Version — Get ₹${a.bonus} Welcome Bonus`,
  (n, a) => `${n} App: Play, Refer & Win with ₹${a.bonus} Free Bonus`,
  (n, a) => `${n} — Instant Withdraw from ₹${a.minWithdraw} on GoGoYonoGames`,
  (n, a) => `${n} 2025 Update: ₹${a.bonus} Joining Bonus & Fast Payout`,
  (n, a) => `Get ${n} APK Now — Claim ₹${a.bonus} Free & UPI Withdraw`,
  (n, a) => `${n} Official APK — Bonus ₹${a.bonus}, Min ₹${a.minWithdraw} Withdraw`,
  (n, a) => `${n} — Real Cash Rummy & Slots App (₹${a.bonus} Free)`,
  (n, a) => `Play ${n} & Grab a ₹${a.bonus} Reward — GoGoYonoGames`,
  (n, a) => `${n} New Version Download — Special ₹${a.bonus} Bonus Offer`,
];

// -------------- Intro paragraph variants (English, first-person friendly) --------------
const introVariants = [
  (n) => `${n} is a homegrown Indian gaming app that packs rummy, spins and mini-games into one clean interface built for mobile-first players.`,
  (n) => `If you love card games and quick spins, ${n} gives you a smooth, ad-light way to play and earn from your phone every day.`,
  (n) => `Made for the Indian audience, ${n} keeps things simple: register once, grab your reward, and start playing the games that fit your mood.`,
  (n) => `Players who want fair matchmaking and quick UPI cashouts are moving to ${n}, and it's easy to see why once you spend a few minutes inside the lobby.`,
  (n) => `${n} blends the fun of traditional rummy tables with modern spin-and-win rounds so a single sitting can go from strategy to sudden wins.`,
  (n) => `Every corner of ${n} — from the sign-up screen to the withdrawal window — has been tuned for Indian users, with UPI, IMPS and NEFT baked in.`,
  (n) => `Whether you are a college student trying it out on weekends or a serious rummy fan, ${n} scales its rooms to match your bankroll and skill level.`,
  (n) => `${n} keeps the on-boarding tiny: a mobile number, an OTP, and you are inside — no long forms, no unnecessary permissions.`,
  (n) => `The moment you open ${n} you'll notice how snappy the tables feel, which is a big deal on entry-level Android phones common in India.`,
  (n) => `${n} is one of those apps you download for the bonus and stay for the community — friendly opponents, live tables and steady daily contests.`,
];

// -------------- Benefit sentence variants (English) --------------
const benefitVariants = [
  (a) => `New members receive a ₹${a.bonus} welcome credit right after OTP verification, and the withdrawal counter unlocks at just ₹${a.minWithdraw}.`,
  (a) => `Grab ₹${a.bonus} as a joining reward, invite friends to earn a lifetime referral cut, and cash out to your bank the moment your balance reaches ₹${a.minWithdraw}.`,
  (a) => `A one-time ₹${a.bonus} sign-up boost, plus low ₹${a.minWithdraw} cashout, keeps the app friendly for beginners who don't want to deposit right away.`,
  (a) => `Along with a ₹${a.bonus} kickoff, players enjoy daily login gifts, spin rewards, and a ₹${a.minWithdraw} minimum withdrawal via UPI.`,
  (a) => `Expect ₹${a.bonus} in your wallet on day one, plus weekly missions and a low ₹${a.minWithdraw} withdrawal threshold that keeps money moving.`,
  (a) => `The ₹${a.bonus} welcome pot, quick KYC, and ₹${a.minWithdraw} instant withdraw make this one of the more player-friendly options out there.`,
  (a) => `Sign-up brings ₹${a.bonus} instantly, referrals stack on top, and cashout starts at only ₹${a.minWithdraw} — no hidden hoops.`,
];

// -------------- Why choose variants (English) --------------
const whyChooseVariants = [
  (n) => `What makes ${n} stand out is its focus on Indian payment rails — UPI, Paytm, PhonePe and direct bank transfers all work without extra steps.`,
  (n) => `Regulars keep coming back to ${n} because of quick withdrawals, fair anti-fraud systems, and a support team that actually replies inside the app.`,
  (n) => `${n} avoids heavy notifications and doesn't nag users with pop-ups, which is refreshing compared to many gaming apps out there.`,
  (n) => `From the way ${n} handles low balances to the way it shows referral earnings, everything feels built for real Indian players and not for global metrics.`,
  (n) => `${n} runs light — under 60 MB on most builds — so even users on 2 GB RAM phones can play smoothly without heat or lag.`,
  (n) => `${n} keeps the classic games you already know (Points Rummy, Deals, Pool) alongside newer spin and arcade rounds for variety.`,
];

// -------------- How-to-install variants (English) --------------
const howVariants = [
  (n) => `Getting ${n} onto your phone takes under a minute: tap the download button on this page, allow install from unknown sources, open the APK, and you're in.`,
  (n) => `To install ${n}, download the APK, open it from your Downloads folder, tap "Install", and finish sign-up with your active mobile number.`,
  (n) => `Grab the ${n} APK from the button above, enable third-party installs in your phone settings, and register with a working mobile number.`,
  (n) => `Installing ${n} is straightforward — download, allow the install, register, verify OTP, and claim your reward before opening any game table.`,
  (n) => `After downloading ${n}, hit the APK file, complete the install, and add UPI or bank details from the wallet screen to unlock full withdrawal limits.`,
];

// -------------- Hindi intro variants (unique per app) --------------
const hindiIntroVariants = [
  (n) => `${n} भारत के मोबाइल gamers के लिए बनाया गया एक तेज़ और आसान Yono style app है, जिसमें Rummy से लेकर Slots तक कई तरह के real cash games मिलते हैं।`,
  (n) => `अगर आप roz कुछ मिनट खेल कर छोटी-मोटी कमाई करना चाहते हैं तो ${n} एक साफ-सुथरा और bhrosemand option है — बिना किसी complicated setup के।`,
  (n) => `${n} का interface Hindi और English दोनों में समझने लायक है, इसलिए new users को भी शुरू करने में कोई परेशानी नहीं आती।`,
  (n) => `${n} में login करते ही आपको daily login bonus, spin wheels, aur referral rewards जैसी छोटी-छोटी कमाई की opportunities लगातार मिलती रहती हैं।`,
  (n) => `Rummy, Teen Patti aur Slot lovers ke लिए ${n} एक balanced app है — तेज़ tables, कम loading time और साफ withdrawal system।`,
  (n) => `${n} ko design kiya gaya hai low-end Android phones ke liye — मतलब 2 GB RAM वाले फोन पर भी यह smooth चलता है।`,
  (n) => `${n} की सबसे बड़ी खासियत है इसका quick UPI payout — जीत का पैसा कुछ ही मिनटों में सीधे आपके bank में आ जाता है।`,
  (n) => `${n} पर आप बिना deposit किए भी शुरुआत कर सकते हैं क्योंकि welcome bonus से ही कई tables खेले जा सकते हैं।`,
];

// -------------- Hindi benefit variants --------------
const hindiBenefitVariants = [
  (a) => `Register करते ही ₹${a.bonus} का सीधा welcome bonus, refer करने पर lifetime commission और सिर्फ ₹${a.minWithdraw} से withdrawal।`,
  (a) => `नए users को ₹${a.bonus} free bonus, daily rewards और सिर्फ ₹${a.minWithdraw} पर UPI withdraw — बिना किसी hidden charge के।`,
  (a) => `पहला ₹${a.bonus} bonus तो instant मिलता ही है, ऊपर से हर दिन के तासक और missions से extra earning का मौका।`,
  (a) => `₹${a.bonus} joining bonus + refer & earn + ₹${a.minWithdraw} minimum withdraw — तीनों मिलकर इसे शुरुआती players के लिए ideal बनाते हैं।`,
  (a) => `Sign-up पर ₹${a.bonus}, referral पर 20% तक commission, aur ₹${a.minWithdraw} से bank withdraw — यही इसका मुख्य आकर्षण है।`,
];

// -------------- Hindi how-to variants --------------
const hindiHowVariants = [
  (n) => `${n} download करने के लिए ऊपर दिए गए button पर tap करें, APK file save होते ही install करें, फिर mobile number से register कर लें।`,
  (n) => `पहले APK download करें, unknown sources से install allow करें, फिर ${n} खोलकर OTP verify करें और तुरंत खेलना शुरू करें।`,
  (n) => `${n} पर account बनाना बहुत आसान है — number डालें, OTP डालें, नाम भरें और bonus wallet में देख लें।`,
  (n) => `Download → Install → Sign-up → OTP verify → Bonus claim — ${n} पर ये पाँच step पूरे करने में 2 मिनट भी नहीं लगते।`,
];

// -------------- FAQ variants (unique per app) --------------
const faqQ1Variants = [
  (n) => `Is ${n} safe to use on my phone?`,
  (n) => `Can I trust ${n} with my bank details?`,
  (n) => `Does ${n} run smoothly on low-end Android?`,
  (n) => `Will ${n} work in my region of India?`,
];
const faqA1Variants = [
  (n) => `${n} uses standard OTP verification and encrypted UPI flow, so your banking details are not exposed to any third party. Always download the APK from a verified page like this one.`,
  (n) => `The team behind ${n} maintains a small support desk and fraud-check system inside the app; balance loads and cashouts are logged and reversible if something looks off.`,
  (n) => `${n} runs fine on phones with 2 GB RAM or above; graphics scale down automatically on entry-level devices to keep tables lag-free.`,
  (n) => `${n} is aimed at Indian players and supports Hindi + English on most screens, along with regional payment methods that work across states.`,
];

const faqQ2Variants = [
  (n) => `How long does withdrawal take on ${n}?`,
  (n) => `When will my winnings from ${n} reach my bank?`,
  (n) => `Are there daily withdrawal limits on ${n}?`,
];
const faqA2Variants = [
  (a) => `Once you cross the ₹${a.minWithdraw} threshold, requests are usually processed within 5–30 minutes on business hours; UPI transfers can be almost instant during off-peak times.`,
  (a) => `Most transfers land in your bank within an hour of hitting the ₹${a.minWithdraw} minimum. Larger requests may need a quick KYC selfie check before payout.`,
  (a) => `There is a daily payout cap tied to your verification level; new users start with a modest limit that expands as you complete KYC and play more.`,
];

const faqQ3Variants = [
  (n) => `Can I earn from ${n} without depositing?`,
  (n) => `Do I need to add money before playing ${n}?`,
];
const faqA3Variants = [
  (a, n) => `Yes — the ₹${a.bonus} welcome credit and daily missions let you play a decent number of hands on ${n} without spending a rupee from your own wallet.`,
  (a, n) => `You can absolutely start bonus-only on ${n}; deposits open up higher-stake tables but are never mandatory to enjoy the app.`,
];

// -------------- Spec table variants --------------
const specSizeVariants = ['38 MB', '42 MB', '47 MB', '51 MB', '55 MB', '60 MB', '68 MB'];
const specVersionVariants = ['1.0.4', '1.1.2', '1.2.0', '1.3.5', '2.0.1', '2.1.0', '2.2.3', '3.0.0'];
const specRatingVariants = ['4.3', '4.4', '4.5', '4.6', '4.7', '4.8'];
const specDownloadsVariants = ['12,000+', '25,000+', '48,000+', '75,000+', '1,20,000+', '2,50,000+', '5,00,000+'];
const specDeveloperVariants = ['Yono Interactive Studios', 'BharatPlay Labs', 'Indus Gaming Works', 'Rummy Craft Studios', 'Playgrid India', 'Chakra Gaming Studio'];


// -------------- Category tag variants --------------
const categoryFor = (name) => {
  const s = name.toLowerCase();
  if (s.includes('rummy')) return 'Rummy';
  if (s.includes('slot') || s.includes('spin')) return 'Slots & Spin';
  if (s.includes('teen patti') || s.includes('patti')) return 'Teen Patti';
  if (s.includes('bet')) return 'Betting Games';
  if (s.includes('777') || s.includes('vip')) return '777 Series';
  if (s.includes('bingo')) return 'Bingo';
  if (s.includes('ludo')) return 'Ludo';
  return 'Yono Games';
};

// -------------- Public API --------------
export const buildAppSeo = (app) => {
  const name = app.name;
  const slug = app.id;

  const title = pick(titleVariants, slug, 'title')(name, app);
  const intro = pick(introVariants, slug, 'intro')(name);
  const benefit = pick(benefitVariants, slug, 'benefit')(app);
  const whyChoose = pick(whyChooseVariants, slug, 'why')(name);
  const howTo = pick(howVariants, slug, 'how')(name);
  const hindiIntro = pick(hindiIntroVariants, slug, 'hi-intro')(name);
  const hindiBenefit = pick(hindiBenefitVariants, slug, 'hi-benefit')(app);
  const hindiHow = pick(hindiHowVariants, slug, 'hi-how')(name);
  const category = categoryFor(name);

  // Per-app FAQs (unique combination per slug)
  const faqs = [
    {
      q: pick(faqQ1Variants, slug, 'q1')(name),
      a: pick(faqA1Variants, slug, 'a1')(name),
    },
    {
      q: pick(faqQ2Variants, slug, 'q2')(name),
      a: pick(faqA2Variants, slug, 'a2')(app),
    },
    {
      q: pick(faqQ3Variants, slug, 'q3')(name),
      a: pick(faqA3Variants, slug, 'a3')(app, name),
    },
  ];

  // Per-app specs (deterministic)
  const specs = {
    size: pick(specSizeVariants, slug, 'size'),
    version: pick(specVersionVariants, slug, 'ver'),
    rating: pick(specRatingVariants, slug, 'rating'),
    downloads: pick(specDownloadsVariants, slug, 'dl'),
    developer: pick(specDeveloperVariants, slug, 'dev'),
    platform: 'Android 5.0+',
    lastUpdated: '2025',
  };

  const description = `${intro} ${benefit}`.slice(0, 300);

  const keywords = [
    name.toLowerCase(),
    `${name.toLowerCase()} download`,
    `${name.toLowerCase()} apk`,
    `${name.toLowerCase()} bonus`,
    category.toLowerCase(),
    'yono earning app',
    'gogoyonogames',
  ].join(', ');

  return {
    title,
    description,
    keywords,
    intro,
    benefit,
    whyChoose,
    howTo,
    hindiIntro,
    hindiBenefit,
    hindiHow,
    category,
    faqs,
    specs,
  };
};
